import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Send, Info, AlertTriangle, Zap, Clock, X, CalendarClock } from "lucide-react";

const PRIORITY_META = {
  info:    { Icon: Info,          color: "text-sky-700",   bg: "bg-sky-50",   label: "Info" },
  warning: { Icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50", label: "Warning" },
  urgent:  { Icon: Zap,           color: "text-rose-700",  bg: "bg-rose-50",  label: "Urgent" },
};

const ROLE_OPTIONS = [
  { value: "school_owner", label: "School Owners" },
  { value: "sub_owner", label: "Sub-Owners" },
  { value: "branch_admin", label: "Branch Admins" },
];

export default function SendNotificationPage() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("info");
  const [link, setLink] = useState("");
  const [targetType, setTargetType] = useState("all");
  const [roles, setRoles] = useState([]);
  const [schoolIds, setSchoolIds] = useState([]);
  const [schools, setSchools] = useState([]);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sendMode, setSendMode] = useState("now"); // "now" | "schedule"
  const [scheduledAtLocal, setScheduledAtLocal] = useState(""); // "YYYY-MM-DDTHH:MM" datetime-local string

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/schools", { params: { page: 1, page_size: 100, status_filter: "approved" } });
        setSchools(res.data?.items || []);
      } catch { /* ignore */ }
      await loadHistory();
      await loadScheduled();
    })();
  }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get("/admin/notifications/sent", { params: { page: 1, page_size: 10 } });
      setHistory(res.data?.items || []);
    } catch { /* ignore */ }
    finally { setLoadingHistory(false); }
  };

  const loadScheduled = async () => {
    try {
      const res = await api.get("/admin/notifications/scheduled");
      setScheduled(res.data?.items || []);
    } catch { /* ignore */ }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm("Cancel this scheduled notification? Recipients will not receive it.")) return;
    try {
      await api.delete(`/admin/notifications/scheduled/${id}`);
      toast.success("Scheduled notification cancelled");
      await loadScheduled();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleRole = (r) =>
    setRoles((arr) => (arr.includes(r) ? arr.filter((x) => x !== r) : [...arr, r]));
  const toggleSchool = (id) =>
    setSchoolIds((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title is required");
    if (!message.trim()) return toast.error("Message is required");
    if (targetType === "roles" && roles.length === 0) return toast.error("Pick at least one role");
    if (targetType === "schools" && schoolIds.length === 0) return toast.error("Pick at least one school");

    let scheduled_at = null;
    if (sendMode === "schedule") {
      if (!scheduledAtLocal) return toast.error("Pick a date/time to schedule");
      // datetime-local strings are in the user's local TZ; convert to ISO UTC.
      const dt = new Date(scheduledAtLocal);
      if (Number.isNaN(dt.getTime())) return toast.error("Invalid date/time");
      if (dt.getTime() <= Date.now()) return toast.error("Pick a future date/time");
      scheduled_at = dt.toISOString();
    }

    setSending(true);
    try {
      const res = await api.post("/notifications/send", {
        title: title.trim(),
        message: message.trim(),
        priority,
        link: link.trim() || null,
        target_type: targetType,
        roles: targetType === "roles" ? roles : [],
        school_ids: targetType === "schools" ? schoolIds : [],
        scheduled_at,
      });
      if (res.data?.scheduled) {
        toast.success(`Scheduled — will deliver to ${res.data?.notification?.recipient_count || 0} recipient(s)`);
      } else {
        toast.success(`Sent to ${res.data?.delivered || 0} recipient(s)`);
      }
      setTitle(""); setMessage(""); setLink("");
      setRoles([]); setSchoolIds([]); setTargetType("all");
      setSendMode("now"); setScheduledAtLocal("");
      await Promise.all([loadHistory(), loadScheduled()]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="space-y-6" data-testid="send-notification-page">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Send Notification</h1>
          <p className="text-sm text-brand-inkMuted mt-1">Broadcast an announcement to owners, sub-owners, and branch admins.</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl border border-[#2563EB]/10 p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="notif-title">Title</Label>
              <Input
                id="notif-title" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., New feature available!"
                maxLength={140}
                data-testid="notif-title-input"
              />
            </div>
            <div>
              <Label htmlFor="notif-priority">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger id="notif-priority" data-testid="notif-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="notif-message">Message</Label>
            <Textarea
              id="notif-message" rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Write the full announcement here…"
              maxLength={2000}
              data-testid="notif-message-input"
            />
            <div className="text-xs text-brand-inkMuted text-right mt-1">{message.length}/2000</div>
          </div>

          <div>
            <Label htmlFor="notif-link">Optional link (URL or app route)</Label>
            <Input
              id="notif-link" value={link} onChange={(e) => setLink(e.target.value)}
              placeholder="/dashboard or https://…"
              data-testid="notif-link-input"
            />
          </div>

          <div className="border-t border-slate-100 pt-4">
            <Label>Send to</Label>
            <div className="mt-2">
              <Select value={targetType} onValueChange={setTargetType}>
                <SelectTrigger data-testid="notif-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone (all owners + sub-owners + branch admins)</SelectItem>
                  <SelectItem value="roles">Specific roles…</SelectItem>
                  <SelectItem value="schools">Specific schools…</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {targetType === "roles" && (
              <div className="mt-3 flex flex-wrap gap-3" data-testid="notif-roles-picker">
                {ROLE_OPTIONS.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 px-3 py-2 rounded-full border border-slate-200 hover:bg-slate-50 cursor-pointer">
                    <Checkbox
                      checked={roles.includes(r.value)}
                      onCheckedChange={() => toggleRole(r.value)}
                      data-testid={`notif-role-${r.value}`}
                    />
                    <span className="text-sm">{r.label}</span>
                  </label>
                ))}
              </div>
            )}

            {targetType === "schools" && (
              <div className="mt-3 max-h-60 overflow-y-auto rounded-2xl border border-slate-200 divide-y" data-testid="notif-schools-picker">
                {schools.length === 0 && <div className="p-4 text-sm text-brand-inkMuted text-center">No approved schools.</div>}
                {schools.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer">
                    <Checkbox
                      checked={schoolIds.includes(s.id)}
                      onCheckedChange={() => toggleSchool(s.id)}
                      data-testid={`notif-school-${s.id}`}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                      <div className="text-xs text-brand-inkMuted truncate">{s.contact_email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Schedule */}
          <div className="border-t border-slate-100 pt-4">
            <Label>When to send</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSendMode("now")}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${sendMode === "now" ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid="notif-mode-now-btn"
              >
                <Send className="w-3.5 h-3.5 inline mr-1.5" /> Send now
              </button>
              <button
                type="button"
                onClick={() => setSendMode("schedule")}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${sendMode === "schedule" ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid="notif-mode-schedule-btn"
              >
                <CalendarClock className="w-3.5 h-3.5 inline mr-1.5" /> Schedule for later
              </button>
            </div>
            {sendMode === "schedule" && (
              <div className="mt-3">
                <Label htmlFor="notif-schedule-at" className="text-xs text-brand-inkMuted">Pick a date and time (your local timezone)</Label>
                <Input
                  id="notif-schedule-at" type="datetime-local"
                  value={scheduledAtLocal}
                  onChange={(e) => setScheduledAtLocal(e.target.value)}
                  data-testid="notif-schedule-at-input"
                  className="max-w-xs"
                />
                {scheduledAtLocal && (
                  <p className="text-xs text-brand-inkMuted mt-1.5">
                    Will deliver at <strong>{new Date(scheduledAtLocal).toLocaleString()}</strong>
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={sending} data-testid="notif-send-btn" className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]">
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Working…" : (sendMode === "schedule" ? "Schedule notification" : "Send notification")}
            </Button>
          </div>
        </form>

        {/* Scheduled (pending) */}
        {scheduled.length > 0 && (
          <div className="bg-white rounded-3xl border border-amber-200 p-6" data-testid="notif-scheduled-list">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-amber-600" />
              <h2 className="font-heading font-bold text-foreground">Scheduled</h2>
              <span className="ml-auto text-xs text-brand-inkMuted">{scheduled.length} pending</span>
            </div>
            <div className="divide-y divide-slate-100">
              {scheduled.map((n) => {
                const meta = PRIORITY_META[n.priority] || PRIORITY_META.info;
                return (
                  <div key={n.id} className="py-3 flex gap-3" data-testid={`scheduled-row-${n.id}`}>
                    <div className={`shrink-0 w-9 h-9 rounded-2xl ${meta.bg} ${meta.color} flex items-center justify-center`}>
                      <meta.Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-foreground">{n.title}</div>
                      <p className="text-xs text-brand-inkMuted line-clamp-2">{n.message}</p>
                      <div className="text-[11px] text-amber-700 mt-1 font-medium">
                        Delivers at {new Date(n.scheduled_at).toLocaleString()} • {n.recipient_count} target(s)
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => cancelScheduled(n.id)}
                      className="shrink-0 self-start rounded-full p-1.5 text-rose-600 hover:bg-rose-50"
                      title="Cancel scheduled notification"
                      data-testid={`cancel-scheduled-${n.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sent history */}
        <div className="bg-white rounded-3xl border border-[#2563EB]/10 p-6">
          <h2 className="font-heading font-bold text-foreground">Recently sent</h2>
          {loadingHistory ? (
            <div className="py-6 text-center text-brand-inkMuted">Loading…</div>
          ) : history.length === 0 ? (
            <div className="py-6 text-center text-sm text-brand-inkMuted">No notifications sent yet.</div>
          ) : (
            <div className="mt-3 divide-y divide-slate-100" data-testid="notif-sent-history">
              {history.map((n) => {
                const meta = PRIORITY_META[n.priority] || PRIORITY_META.info;
                return (
                  <div key={n.id} className="py-3 flex gap-3">
                    <div className={`shrink-0 w-9 h-9 rounded-2xl ${meta.bg} ${meta.color} flex items-center justify-center`}>
                      <meta.Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-foreground">{n.title}</div>
                      <p className="text-xs text-brand-inkMuted line-clamp-2">{n.message}</p>
                      <div className="text-[11px] text-slate-400 mt-1">
                        {new Date(n.created_at).toLocaleString()} • {n.recipient_count} sent • {n.read_count} read
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
